import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import usePageMeta from '@/hooks/usePageMeta';

const DankePage = () => {
  usePageMeta({
    title: 'Vielen Dank für Ihre Anfrage | gowiTEC',
    description: 'Ihre Nachricht wurde erfolgreich übermittelt. Das gowiTEC-Team meldet sich schnellstmöglich bei Ihnen.',
    keywords: 'gowiTEC Anfrage gesendet, Kontakt bestätigt',
    canonical: 'https://gowitec.de/danke',
  });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const type = searchParams.get('typ') ?? 'kontakt';

  const isAnfrage = type === 'anfrage';

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const steps = [
    { icon: 'ri-checkbox-circle-line', label: isAnfrage ? 'Anfrage eingegangen' : 'Nachricht eingegangen', done: true },
    { icon: 'ri-search-eye-line', label: 'Prüfung durch unser Team', done: false },
    { icon: 'ri-discuss-line', label: isAnfrage ? 'Erstgespräch & Angebot' : 'Persönliche Rückmeldung', done: false },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <main className="flex-1 flex flex-col">
        {/* Hero-Streifen */}
        <section className="bg-[#0f1f4a] pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-[#DC2626]"></span>
              <span className="text-[#DC2626] text-xs font-semibold tracking-widest uppercase">
                {isAnfrage ? 'Projektanfrage' : 'Kontakt'}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Vielen Dank!
            </h1>
          </div>
        </section>

        {/* Hauptinhalt */}
        <section className="flex-1 py-20 bg-[#F8FAFC]">
          <div className="max-w-3xl mx-auto px-6 md:px-10">

            {/* Erfolgs-Card */}
            <div className="bg-white rounded-2xl border border-gray-100 p-10 md:p-14 text-center mb-10">
              {/* Checkmark-Animation */}
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="w-24 h-24 rounded-full bg-green-50 flex items-center justify-center animate-[ping_1s_ease-out_1]">
                </div>
                <div className="absolute inset-0 w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-check-line text-green-500 text-4xl"></i>
                </div>
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                {isAnfrage ? 'Ihre Projektanfrage ist bei uns!' : 'Ihre Nachricht ist angekommen!'}
              </h2>
              <p className="text-gray-500 text-base leading-relaxed max-w-md mx-auto mb-8">
                {isAnfrage
                  ? 'Wir haben Ihre Anfrage erhalten und melden uns innerhalb von 24 Stunden mit einer ersten Einschätzung bei Ihnen.'
                  : 'Wir haben Ihre Nachricht erhalten und werden uns so schnell wie möglich bei Ihnen melden.'}
              </p>

              {/* Nächste Schritte */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                {steps.map((step, i) => (
                  <div key={step.label} className="flex items-center gap-3 sm:gap-0">
                    <div className={`flex flex-col items-center gap-2 px-4 ${step.done ? 'opacity-100' : 'opacity-50'}`}>
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full ${step.done ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <i className={`${step.icon} text-lg ${step.done ? 'text-green-600' : 'text-gray-400'}`}></i>
                      </div>
                      <span className="text-xs text-gray-600 font-medium text-center leading-tight max-w-[90px]">{step.label}</span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className="hidden sm:block w-12 h-0.5 bg-gray-200 mx-1 shrink-0"></div>
                    )}
                  </div>
                ))}
              </div>

              {/* Kontakt-Hinweis */}
              <div className="bg-[#F8FAFC] rounded-xl p-5 flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
                <a
                  href="tel:+491776512122"
                  className="flex items-center gap-3 cursor-pointer group"
                  aria-label="Telefon: +49 1776512122"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                    <i className="ri-phone-line text-[#1E3A8A] text-lg"></i>
                  </div>
                  <div className="text-left">
                    <div className="text-gray-400 text-xs">Dringende Fragen?</div>
                    <div className="text-gray-900 font-semibold text-sm group-hover:text-[#1E3A8A] transition-colors">+49 1776512122</div>
                  </div>
                </a>
                <div className="hidden sm:block w-px h-10 bg-gray-200"></div>
                <a
                  href="mailto:anfrage@gowitec.de"
                  className="flex items-center gap-3 cursor-pointer group"
                  aria-label="E-Mail: anfrage@gowitec.de"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                    <i className="ri-mail-line text-[#1E3A8A] text-lg"></i>
                  </div>
                  <div className="text-left">
                    <div className="text-gray-400 text-xs">Per E-Mail</div>
                    <div className="text-gray-900 font-semibold text-sm group-hover:text-[#1E3A8A] transition-colors">anfrage@gowitec.de</div>
                  </div>
                </a>
              </div>

              {/* Countdown + Buttons */}
              <p className="text-gray-400 text-xs mb-5">
                Sie werden in <span className="font-semibold text-gray-600">{countdown}</span> Sekunden automatisch zur Startseite weitergeleitet.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="bg-[#1E3A8A] text-white text-sm font-semibold px-7 py-3 rounded-md hover:bg-[#1e40af] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Zur Startseite
                </Link>
                <Link
                  to="/leistungen"
                  className="border border-gray-200 text-gray-700 text-sm font-medium px-7 py-3 rounded-md hover:border-[#1E3A8A] hover:text-[#1E3A8A] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Unsere Leistungen
                </Link>
              </div>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DankePage;
